import React, { useState } from "react";
import { useQuery } from "convex/react";
import { api } from "../../../../../convex/_generated/api";
import { getPersonaTheme } from "./personaTheme";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  TrendingUp,
  Clock,
  DollarSign,
  Zap,
  Bot,
  ArrowRight,
  Sparkles,
  Loader2,
  Calendar,
} from "lucide-react";

interface ProductivitySpendModalProps {
  isOpen: boolean;
  onClose: () => void;
  orgId?: string;
  personalWorkspaceId?: string;
  onSelectAgentUsage: (personaKey: string) => void;
}

export function ProductivitySpendModal({
  isOpen,
  onClose,
  orgId,
  personalWorkspaceId,
  onSelectAgentUsage,
}: ProductivitySpendModalProps) {
  const [days, setDays] = useState(30);

  const roiData = useQuery(
    api.aiWorkforce.roiMetrics.getWorkforceUsageAndROI,
    isOpen
      ? {
          orgId: orgId as any,
          personalWorkspaceId: personalWorkspaceId as any,
          days,
        }
      : "skip"
  );

  const overall = roiData?.overall;
  const agents = roiData?.byAgent || [];

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-4xl max-h-[85vh] flex flex-col p-0 gap-0 overflow-hidden bg-white">
        {/* Header */}
        <DialogHeader className="p-6 pb-4 border-b border-gray-100 flex-shrink-0 bg-slate-50/60">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-emerald-100 text-emerald-700 flex items-center justify-center shadow-xs">
                <TrendingUp className="w-5 h-5" />
              </div>
              <div>
                <DialogTitle className="text-xl font-bold text-gray-900">
                  Workforce Productivity & Spend ROI
                </DialogTitle>
                <DialogDescription className="text-sm text-gray-500 mt-0.5">
                  Track human hours saved, labor value created, and AI credit return on investment.
                </DialogDescription>
              </div>
            </div>

            {/* Period Filters */}
            <div className="flex items-center gap-1.5 bg-white p-1 rounded-xl border border-gray-200 self-start sm:self-auto">
              <button
                onClick={() => setDays(7)}
                className={`px-3 py-1 text-xs font-semibold rounded-lg transition-all ${
                  days === 7 ? "bg-emerald-600 text-white shadow-2xs" : "text-gray-600 hover:text-gray-900"
                }`}
              >
                7 Days
              </button>
              <button
                onClick={() => setDays(30)}
                className={`px-3 py-1 text-xs font-semibold rounded-lg transition-all ${
                  days === 30 ? "bg-emerald-600 text-white shadow-2xs" : "text-gray-600 hover:text-gray-900"
                }`}
              >
                30 Days
              </button>
              <button
                onClick={() => setDays(90)}
                className={`px-3 py-1 text-xs font-semibold rounded-lg transition-all ${
                  days === 90 ? "bg-emerald-600 text-white shadow-2xs" : "text-gray-600 hover:text-gray-900"
                }`}
              >
                90 Days
              </button>
            </div>
          </div>
        </DialogHeader>

        {/* Content Body */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6 custom-scrollbar min-h-[350px]">
          {roiData === undefined ? (
            <div className="py-16 flex flex-col items-center justify-center text-gray-400">
              <Loader2 className="w-8 h-8 animate-spin text-emerald-600 mb-3" />
              <p className="text-sm font-medium">Calculating workforce productivity & hours saved...</p>
            </div>
          ) : (
            <>
              {/* Executive Summary Cards */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3.5">
                <div className="p-4 rounded-xl bg-gradient-to-br from-emerald-50 to-white border border-emerald-200/80 shadow-xs">
                  <div className="flex items-center justify-between text-emerald-700">
                    <span className="text-xs font-bold uppercase tracking-wider">Human Hours Saved</span>
                    <Clock className="w-4 h-4" />
                  </div>
                  <div className="text-2xl font-bold text-gray-900 mt-2">
                    {overall?.totalHoursSaved || 0} <span className="text-sm font-normal text-gray-500">hrs</span>
                  </div>
                  <p className="text-2xs text-gray-500 mt-1">
                    Based on manual operational benchmarks
                  </p>
                </div>

                <div className="p-4 rounded-xl bg-gradient-to-br from-blue-50 to-white border border-blue-200/80 shadow-xs">
                  <div className="flex items-center justify-between text-blue-700">
                    <span className="text-xs font-bold uppercase tracking-wider">Labor Value Saved</span>
                    <DollarSign className="w-4 h-4" />
                  </div>
                  <div className="text-2xl font-bold text-gray-900 mt-2">
                    ${overall?.totalLaborValueSaved?.toLocaleString() || "0"}
                  </div>
                  <p className="text-2xs text-gray-500 mt-1">
                    Offset @ $35/hr back-office benchmark
                  </p>
                </div>

                <div className="p-4 rounded-xl bg-gradient-to-br from-amber-50 to-white border border-amber-200/80 shadow-xs">
                  <div className="flex items-center justify-between text-amber-700">
                    <span className="text-xs font-bold uppercase tracking-wider">Workforce Spend</span>
                    <Zap className="w-4 h-4" />
                  </div>
                  <div className="text-2xl font-bold text-gray-900 mt-2">
                    {overall?.totalCredits?.toLocaleString() || 0} <span className="text-sm font-normal text-gray-500">pts (${overall?.totalSpendDollars?.toFixed(2) || "0.00"})</span>
                  </div>
                  <p className="text-2xs text-gray-500 mt-1">
                    Total credits deducted for agent tasks
                  </p>
                </div>

                <div className="p-4 rounded-xl bg-gradient-to-br from-purple-50 to-white border border-purple-200/80 shadow-xs">
                  <div className="flex items-center justify-between text-purple-700">
                    <span className="text-xs font-bold uppercase tracking-wider">Net Workforce ROI</span>
                    <Sparkles className="w-4 h-4" />
                  </div>
                  <div className="text-2xl font-bold text-purple-900 mt-2">
                    {overall?.netWorkforceROI ? `${overall.netWorkforceROI}%` : "100%"}
                  </div>
                  <p className="text-2xs text-purple-700 font-medium mt-1">
                    Value generated vs credit investment
                  </p>
                </div>
              </div>

              {/* Agent Performance Leaderboard */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider">
                    Productivity Breakdown by Agent
                  </h3>
                  <span className="text-xs text-gray-500">
                    Click any agent to inspect detailed execution logs
                  </span>
                </div>

                <div className="grid grid-cols-1 gap-2.5">
                  {agents.map((agent: any) => {
                    const theme = getPersonaTheme(agent.personaKey);

                    return (
                      <div
                        key={agent.personaKey}
                        onClick={() => {
                          onSelectAgentUsage(agent.personaKey);
                          onClose();
                        }}
                        className="p-4 rounded-xl border border-gray-200 hover:border-blue-300 hover:shadow-xs transition-all bg-white flex flex-col sm:flex-row sm:items-center justify-between gap-4 cursor-pointer group"
                      >
                        <div className="flex items-center gap-3.5 min-w-0">
                          <div
                            className={`w-11 h-11 rounded-xl ${theme.bgLight} border ${theme.border} ${theme.text} flex items-center justify-center flex-shrink-0 shadow-2xs`}
                          >
                            <Bot className="w-5 h-5" />
                          </div>

                          <div className="min-w-0">
                            <div className="flex items-center gap-2">
                              <h4 className="text-base font-bold text-gray-900 group-hover:text-blue-600 transition-colors">
                                {agent.name}
                              </h4>
                              <span className="text-xs text-gray-500 font-medium">({agent.title})</span>
                            </div>
                            <p className="text-xs text-gray-500 mt-0.5 truncate">
                              Primary Output: <span className="font-semibold text-gray-700">{agent.taskTitle}</span>
                            </p>
                          </div>
                        </div>

                        <div className="flex items-center gap-6 justify-between sm:justify-end border-t sm:border-t-0 pt-2 sm:pt-0 border-gray-100 flex-shrink-0">
                          <div className="text-right">
                            <span className="text-xs text-gray-500 block">Hours Saved</span>
                            <span className="text-sm font-bold text-emerald-700">
                              {agent.hoursSaved} hrs
                            </span>
                          </div>

                          <div className="text-right">
                            <span className="text-xs text-gray-500 block">Spend</span>
                            <span className="text-sm font-bold text-gray-900">
                              {agent.creditsUsed} pts
                            </span>
                          </div>

                          <div className="text-right">
                            <span className="text-xs text-gray-500 block">Labor Value</span>
                            <span className="text-sm font-bold text-blue-700">
                              ${agent.laborValueSaved}
                            </span>
                          </div>

                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 px-2.5 text-xs text-blue-600 gap-1 opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <span>Inspect</span>
                            <ArrowRight className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-gray-100 bg-gray-50 flex items-center justify-between text-xs text-gray-500">
          <span>
            Labor savings calculated using commercial cleaning operational benchmarks.
          </span>
          <Button variant="outline" size="sm" onClick={onClose}>
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
